// Types for material overrides
// Two formats coexist:
// 1. Resolver-compatible (V2 calc): { "box_panel": "material-uuid", "shelf": "material-uuid" }
// 2. Frame builder (_frame_ prefix): { "_frame_SidePanels": { materialId: "uuid", composite: ... } }

export interface MaterialOverrideValue {
  materialId: string;
  composite?: {
    compositeId: string;
    combinedThickness: number;
  } | null;
}

// The overrides map can contain both string values (resolver format) and object values (frame format)
export type MaterialOverridesMap = Record<string, string | MaterialOverrideValue>;

/** Maps display label → resolver override key (used by material-resolver.service.ts) */
const PANEL_TO_RESOLVER_KEY: Record<string, string> = {
  'Box Panel': 'box_panel',
  'Door Panel': 'door',
  'Back Panel': 'back_panel',
  'Shelf': 'shelf',
  'Edgeband': 'edgeband',
  'Stretcher': 'stretcher',
  'Drawer Box': 'drawer_box',
};

/** Maps resolver key → display label (reverse of above) */
const RESOLVER_KEY_TO_PANEL: Record<string, string> = {};
for (const [label, key] of Object.entries(PANEL_TO_RESOLVER_KEY)) {
  RESOLVER_KEY_TO_PANEL[key] = label;
}

// Standard panel types derived from FK fields on LineItem entity.
// Note: Some fields (edgeband_material_id, frame_template_id, stretcher_spec,
// drawer_box_spec) exist on the server entity but are NOT in the shared Zod
// schema. They arrive at runtime via API responses, so we use `as any` casts.
const FK_PANEL_MAP: Record<string, string> = {
  box_material_id: 'Box Panel',
  door_material_id: 'Door Panel',
  back_material_id: 'Back Panel',
  shelf_material_id: 'Shelf',
  edgeband_material_id: 'Edgeband',
};

const EXTRA_PANEL_TYPES = ['Stretcher', 'Drawer Box'];

/**
 * Discover panel types for a line item by parsing frame_config JSON,
 * existing material_overrides keys, and falling back to FK-based detection.
 */
export function discoverPanelTypes(lineItem: {
  frame_config?: string | null;
  material_overrides?: string;
  box_material_id?: string | null;
  door_material_id?: string | null;
  back_material_id?: string | null;
  shelf_material_id?: string | null;
  edgeband_material_id?: string | null;
  stretcher_spec?: string | null;
  drawer_box_spec?: string | null;
  num_doors?: number | null;
  num_drawers?: number | null;
  num_shelves?: number | null;
  has_back?: boolean | null;
}): string[] {
  const types = new Set<string>();

  // 1. Parse frame_config if present — extract panel types from frame builder
  let isFrameAssembly = false;
  if (lineItem.frame_config) {
    try {
      const config = JSON.parse(lineItem.frame_config);

      // Structural panels from freePanels
      for (const fp of config?.freePanels || []) {
        const pt = fp?.panelType || fp?.partType;
        if (pt) { types.add(pt); isFrameAssembly = true; }
      }
      // From renderedMeshes
      for (const mesh of config?.renderedMeshes || []) {
        const pt = mesh?.panelType || mesh?.partType;
        if (pt) { types.add(pt); isFrameAssembly = true; }
      }

      // Face component panel types come from nesting data (specific types like
      // "Drawer Front", "Drawer Box Side") — don't add generic "Drawer"/"Door"
      // from zone assignments as they conflict with nesting-derived types.
    } catch {
      // Malformed JSON — fall through to component-count detection
    }
  }

  // For frame assemblies, the frame_config defines the complete panel set — skip fallbacks
  if (isFrameAssembly) {
    // Only add _frame_ overrides from material_overrides (not standard resolver keys)
    if (lineItem.material_overrides && lineItem.material_overrides !== '{}') {
      try {
        const overrides = JSON.parse(lineItem.material_overrides);
        for (const key of Object.keys(overrides)) {
          if (key.startsWith('_frame_')) {
            let pt = key.replace('_frame_', '');
            // Normalize old "panel (PanelType)" format to just "PanelType"
            const parenMatch = pt.match(/\(([^)]+)\)$/);
            if (parenMatch) pt = parenMatch[1];
            types.add(pt);
          }
        }
      } catch { /* ignore */ }
    }
    return Array.from(types).sort();
  }

  // 2. Non-frame items: parse existing material_overrides keys
  if (lineItem.material_overrides && lineItem.material_overrides !== '{}') {
    try {
      const overrides = JSON.parse(lineItem.material_overrides);
      for (const key of Object.keys(overrides)) {
        if (key.startsWith('_frame_')) {
          types.add(key.replace('_frame_', ''));
        } else if (RESOLVER_KEY_TO_PANEL[key]) {
          types.add(RESOLVER_KEY_TO_PANEL[key]);
        }
      }
    } catch {
      // Ignore parse errors
    }
  }

  // 3. Derive from actual component counts on the line item
  if (types.size === 0) {
    const item = lineItem as any;

    types.add('Box Panel');
    types.add('Stretcher');

    if (item.has_back !== false) {
      types.add('Back Panel');
    }
    if ((item.num_shelves ?? 0) > 0) {
      types.add('Shelf');
    }
    if ((item.num_doors ?? 0) > 0) {
      types.add('Door Panel');
    }
    if ((item.num_drawers ?? 0) > 0) {
      types.add('Drawer Box');
    }
    types.add('Edgeband');
  }

  return Array.from(types).sort();
}

/**
 * Build the override key for a panel type.
 * Standard panel types use resolver-compatible keys (e.g., "box_panel", "shelf").
 * Frame builder panel types use _frame_ prefix (e.g., "_frame_SidePanels").
 */
export function overrideKey(panelType: string): string {
  return PANEL_TO_RESOLVER_KEY[panelType] || `_frame_${panelType}`;
}

/**
 * Check if a panel type is a standard type (uses resolver-compatible key)
 */
export function isStandardPanel(panelType: string): boolean {
  return panelType in PANEL_TO_RESOLVER_KEY;
}

/**
 * Parse material_overrides JSON string into a typed map
 */
export function parseOverrides(json: string | undefined | null): MaterialOverridesMap {
  if (!json || json === '{}') return {};
  try {
    const raw: MaterialOverridesMap = JSON.parse(json);
    // Normalize old "_frame_panel (PanelType)" keys to "_frame_PanelType"
    const normalized: MaterialOverridesMap = {};
    for (const [key, value] of Object.entries(raw)) {
      if (key.startsWith('_frame_')) {
        const pt = key.replace('_frame_', '');
        const parenMatch = pt.match(/\(([^)]+)\)$/);
        if (parenMatch) {
          // Old format: _frame_panel (Side Panel) → normalize to _frame_Side Panel
          const newKey = `_frame_${parenMatch[1]}`;
          // Only use old key if new key doesn't already exist
          if (!(newKey in raw)) {
            normalized[newKey] = value;
          }
          continue; // skip the old key
        }
      }
      normalized[key] = value;
    }
    return normalized;
  } catch {
    return {};
  }
}

/**
 * Get the material ID from an override value (handles both string and object formats)
 */
export function getMaterialIdFromOverride(value: string | MaterialOverrideValue | undefined | null): string | null {
  if (!value) return null;
  if (typeof value === 'string') return value;
  return value.materialId || null;
}
